import {
  externalLaunchProviderAtom,
  externalLaunchTokenAtom,
  setExternalLaunchDetails,
} from '@/atoms/externalLaunchAuthAtoms';
import {
  getPersistedMdAuthToken,
  MedicalDirectorSidebar,
  setPersistedMdAuthToken,
} from '@/integrations/medical-director/medicalDirectorSidebar';
import { setAuthProviderAsSessionCookie, setToken } from '@/lib/authActions';
import { type RootState } from '@/store';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { logger } from '@/utils/logger/logger';
import { extractExpFromAccessToken } from '@heidi/auth/tokens';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

// MD-only: restores the persisted Sidebar.Settings token on first mount, bridges
// the live provider-session token (`auth.token`, written by the dual-run cache
// mirror on initial Kinde / Auth0 login) into the external-launch token, and
// mirrors external-launch changes back to Sidebar.Settings. MD Clinical's
// host WebView2 wipes cookies + localStorage when the widget closes — the
// Sidebar.Settings store survives that wipe, so we use it as the cross-launch
// persistence path. No-op outside MD (the persistence helpers and the bridge
// effect's isMedicalDirector() guard short-circuit non-MD users).
//
// Three effects:
// 1. Restore on mount — Sidebar.Settings → external-launch token (+ auth, for
//    migration-window backward-compat with legacy state.auth.token consumers).
// 2. Bridge auth.token → external-launch token — captures the initial Kinde / Auth0
//    login token from the cache's dual-run mirror so it can reach Sidebar.Settings
//    via Effect 3. Gated by isMedicalDirector() so non-MD users don't populate
//    it (which would re-route their requests through the carve-out
//    gate instead of the cache).
// 3. Live-mirror the external-launch token → Sidebar.Settings.
//
// Race-window note: Effect 2 fires after React commits the next render, leaving
// a microsecond gap between cache.applySuccessOutcome writing auth.token and
// the external-launch token catching up. The interceptor's token-presence gate falls
// through to the cache during the gap, returning the same fresh token —
// correct. A force-refresh retry that races the gap can read a stale
// external-launch token and force-logout. Unlikely in practice (Kinde TTL is
// ~1 hour; rotation while the widget is open is rare). Deferred mitigation:
// have applySuccessOutcome write the external-launch token directly in MD context.
export const useMdAuthPersistence = () => {
  const authToken = useSelector((state: RootState) => state.auth.token);
  const authProvider = useSelector((state: RootState) => state.auth.authProvider);
  const externalToken = useAtomValue(externalLaunchTokenAtom);
  const externalProvider = useAtomValue(externalLaunchProviderAtom);

  const hasAttemptedAuthRestoreRef = useRef(false);

  // Effect 1 — restore on mount: Sidebar.Settings → external-launch token (+ auth).
  useEffect(() => {
    if (hasAttemptedAuthRestoreRef.current) return;
    hasAttemptedAuthRestoreRef.current = true;

    // Defense-in-depth: `getPersistedMdAuthToken()` already short-circuits
    // outside MD, but if a stale Sidebar.Settings record ever appears in a
    // non-MD context (test fixtures with mocked persistence, dev envs with
    // leftover state, a future refactor that loosens the helper's gate),
    // skipping this check would propagate the stale token into
    // the external-launch token and the interceptor's presence gate would attach
    // it to every authed request. Matches the gate pattern in Effects 2/3.
    if (!MedicalDirectorSidebar.isMedicalDirector()) return;

    if (externalToken || authToken) return;

    const persisted = getPersistedMdAuthToken();
    if (!persisted?.token) return;
    if (!persisted.expiresAt || persisted.expiresAt <= Date.now()) return;
    if (
      !persisted.authProvider ||
      !Object.values(AuthProvider).includes(persisted.authProvider)
    ) {
      return;
    }

    logger.debug('[MD]', 'Restoring persisted auth token from Sidebar.Settings');
    setExternalLaunchDetails({
      token: persisted.token,
      provider: persisted.authProvider,
      launchContext: null,
    });
    setAuthProviderAsSessionCookie(persisted.authProvider);
    // Backward-compat dual-write: keep state.auth.token populated so
    // legacy consumers still see the restored token.
    setToken(persisted.token);
  }, [externalToken, authToken]);

  // Effect 2 — bridge auth.token → external-launch token, MD context only.
  // Captures initial Kinde / Auth0 login (cache mirror writes auth.token) and
  // any in-session rotation. Debounced by token equality to avoid re-dispatch
  // loops via Effect 3.
  useEffect(() => {
    if (!authToken || !authProvider) return;
    if (!MedicalDirectorSidebar.isMedicalDirector()) return;
    if (externalToken === authToken) return;
    setExternalLaunchDetails({
      token: authToken,
      provider: authProvider,
      launchContext: null,
    });
  }, [authToken, authProvider, externalToken]);

  // Effect 3 — live-mirror the external-launch token → Sidebar.Settings.
  useEffect(() => {
    if (!externalToken || !externalProvider) return;
    if (!MedicalDirectorSidebar.isMedicalDirector()) return;
    setPersistedMdAuthToken({
      token: externalToken,
      expiresAt: extractExpFromAccessToken(externalToken) ?? null,
      authProvider: externalProvider,
    });
  }, [externalToken, externalProvider]);
};
